/**
 * Select > Modify operations on a document-sized coverage buffer (0..255,
 * row-major, y from top). Every function is pure: it reads `data` and returns
 * a new `Uint8Array`, never mutating its input or touching the DOM/GL.
 */

const INSIDE_THRESHOLD = 128;
/**
 * Finite stand-in for infinity. Real IEEE `Infinity` makes the parabola
 * algorithm below compute `Infinity - Infinity` (NaN) whenever two
 * non-source columns/rows are compared, which happens for a fully inside or
 * fully outside buffer. Any value far larger than the largest possible
 * squared distance (width/height would have to exceed ~30 million) is
 * indistinguishable from infinity for our thresholding purposes.
 */
const FAR = 1e15;

function thresholdMask(data: Uint8Array): Uint8Array {
  const mask = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i += 1) mask[i] = data[i] >= INSIDE_THRESHOLD ? 1 : 0;
  return mask;
}

function invertMask(mask: Uint8Array): Uint8Array {
  const out = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i += 1) out[i] = mask[i] ? 0 : 1;
  return out;
}

/**
 * Exact 1D squared distance transform (Felzenszwalb & Huttenlocher): for a
 * cost array `f`, returns for every index the minimum of `f[q] + (p-q)^2`
 * over all `q`. Used twice per axis to build the 2D transform below.
 */
function distanceTransform1D(f: Float64Array, n: number): Float64Array {
  const d = new Float64Array(n);
  const v = new Int32Array(n);
  const z = new Float64Array(n + 1);
  let k = 0;
  v[0] = 0;
  z[0] = -FAR;
  z[1] = FAR;
  for (let q = 1; q < n; q += 1) {
    let s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (s <= z[k]) {
      k -= 1;
      s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    }
    k += 1;
    v[k] = q;
    z[k] = s;
    z[k + 1] = FAR;
  }
  k = 0;
  for (let q = 0; q < n; q += 1) {
    while (z[k + 1] < q) k += 1;
    const dq = q - v[k];
    d[q] = dq * dq + f[v[k]];
  }
  return d;
}

/**
 * Exact squared Euclidean distance transform, two 1D passes over rows then
 * columns (O(width*height), no per-pixel radius search). Returns, for every
 * pixel, the squared distance to the nearest pixel where `inside` is falsy.
 *
 * `outsideIsInside` controls what lies beyond the canvas edge: `true` treats
 * it as more of `inside` (so the edge itself never manufactures a nearby
 * "outside" source — this is what keeps Contract from eating a selection
 * that touches the canvas border); `false` treats it as background, adding a
 * virtual zero-cost source just past every edge.
 */
function squaredDistanceTransform(
  inside: Uint8Array,
  width: number,
  height: number,
  outsideIsInside: boolean,
): Float64Array {
  const cost = new Float64Array(width * height);
  for (let i = 0; i < cost.length; i += 1) cost[i] = inside[i] ? FAR : 0;

  const columns = new Float64Array(width * height);
  const colBuf = new Float64Array(height);
  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < height; y += 1) colBuf[y] = cost[y * width + x];
    const d = distanceTransform1D(colBuf, height);
    if (!outsideIsInside) {
      for (let y = 0; y < height; y += 1) {
        const edge = Math.min((y + 1) * (y + 1), (height - y) * (height - y));
        d[y] = Math.min(d[y], edge);
      }
    }
    for (let y = 0; y < height; y += 1) columns[y * width + x] = d[y];
  }

  const out = new Float64Array(width * height);
  const rowBuf = new Float64Array(width);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) rowBuf[x] = columns[y * width + x];
    const d = distanceTransform1D(rowBuf, width);
    if (!outsideIsInside) {
      for (let x = 0; x < width; x += 1) {
        const edge = Math.min((x + 1) * (x + 1), (width - x) * (width - x));
        d[x] = Math.min(d[x], edge);
      }
    }
    for (let x = 0; x < width; x += 1) out[y * width + x] = d[x];
  }
  return out;
}

function isDegenerate(data: Uint8Array, width: number, height: number): boolean {
  return width <= 0 || height <= 0 || data.length === 0;
}

/** Distance from the nearest inside pixel is <= radius, hard-edged (0/255). */
export function expandCoverage(
  data: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  if (isDegenerate(data, width, height) || radius <= 0) return data.slice();
  const outside = invertMask(thresholdMask(data));
  const distanceToInside = squaredDistanceTransform(outside, width, height, true);
  const r2 = radius * radius;
  const out = new Uint8Array(data.length);
  for (let i = 0; i < out.length; i += 1) out[i] = distanceToInside[i] <= r2 ? 255 : 0;
  return out;
}

/**
 * Inside pixels whose distance from the nearest outside pixel is <= radius
 * turn outside. Out-of-canvas space counts as inside, matching Photoshop:
 * a selection touching (or filling) the canvas never erodes at that edge.
 */
export function contractCoverage(
  data: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  if (isDegenerate(data, width, height) || radius <= 0) return data.slice();
  const inside = thresholdMask(data);
  const distanceToOutside = squaredDistanceTransform(inside, width, height, true);
  const r2 = radius * radius;
  const out = new Uint8Array(data.length);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = inside[i] && distanceToOutside[i] > r2 ? 255 : 0;
  }
  return out;
}

/** A band straddling the selection edge, `thickness` pixels wide, hard-edged. */
export function borderCoverage(
  data: Uint8Array,
  width: number,
  height: number,
  thickness: number,
): Uint8Array {
  if (isDegenerate(data, width, height) || thickness <= 0) return data.slice();
  const inside = thresholdMask(data);
  const outside = invertMask(inside);
  const distanceToOutside = squaredDistanceTransform(inside, width, height, true);
  const distanceToInside = squaredDistanceTransform(outside, width, height, true);
  const half2 = (thickness / 2) * (thickness / 2);
  const out = new Uint8Array(data.length);
  for (let i = 0; i < out.length; i += 1) {
    const within = inside[i] ? distanceToOutside[i] <= half2 : distanceToInside[i] <= half2;
    out[i] = within ? 255 : 0;
  }
  return out;
}

/**
 * Morphological close (fills small notches/gaps) then open (removes small
 * spikes), both with the same radius — expand, contract, contract, expand.
 */
export function smoothCoverage(
  data: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  if (isDegenerate(data, width, height) || radius <= 0) return data.slice();
  const closed = contractCoverage(
    expandCoverage(data, width, height, radius),
    width,
    height,
    radius,
  );
  return expandCoverage(contractCoverage(closed, width, height, radius), width, height, radius);
}

function gaussianKernel(sigma: number): Float64Array {
  const radius = Math.max(1, Math.ceil(sigma * 3));
  const kernel = new Float64Array(radius * 2 + 1);
  let sum = 0;
  for (let i = -radius; i <= radius; i += 1) {
    const w = Math.exp(-(i * i) / (2 * sigma * sigma));
    kernel[i + radius] = w;
    sum += w;
  }
  for (let i = 0; i < kernel.length; i += 1) kernel[i] /= sum;
  return kernel;
}

/**
 * Separable Gaussian blur, sigma = radius/3, edges clamped to the nearest
 * canvas pixel rather than fading — Photoshop's feather does not darken the
 * selection near the document border.
 */
export function featherCoverage(
  data: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  if (isDegenerate(data, width, height) || radius <= 0.5) return data.slice();
  const kernel = gaussianKernel(radius / 3);
  const half = (kernel.length - 1) / 2;

  const horizontal = new Float64Array(data.length);
  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      for (let k = -half; k <= half; k += 1) {
        const sx = Math.min(width - 1, Math.max(0, x + k));
        sum += data[row + sx] * kernel[k + half];
      }
      horizontal[row + x] = sum;
    }
  }

  const out = new Uint8Array(data.length);
  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < height; y += 1) {
      let sum = 0;
      for (let k = -half; k <= half; k += 1) {
        const sy = Math.min(height - 1, Math.max(0, y + k));
        sum += horizontal[sy * width + x] * kernel[k + half];
      }
      out[y * width + x] = Math.round(sum);
    }
  }
  return out;
}
